import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { BackButton } from '~/components/BackButton';
import { Card } from '~/components/Card';
import { Pagination } from '~/components/Pagination';
import {
    FilterBadges,
    PatientRedirect,
    ResultsHeader,
    ResultsRow,
    ResultsRowFallback,
} from '~/components/SearchResults';

import { Select } from '~/components/Select';
import { SpinnerOverlay } from '~/components/SpinnerOverlay';
import {
    SearchColumn,
    SearchContext,
    SearchContextType,
    ServicesContext,
    colFieldMap,
    useSearchClinic,
    useSearchPCP,
    useSearchSort,
} from '~/services';
import {
    PaginationType,
    convertPagination,
    hasUnsavedFilters,
    logError,
    populateSearchFromQuery,
    prepareSearchQueryString,
    useLocationFilters,
} from '~/util';
import { SearchFiltersModal } from '~/components/SearchResults/SearchFiltersModal';

export interface LabeledColumnField {
    label: SearchColumn;
    field: string;
}
export interface SearchResultsProps {
    reportYear: string;
}
export function SearchResults({ reportYear }: SearchResultsProps) {
    const context = React.useContext(ServicesContext);
    const {
        searchValues,
        setSearchValues,
        paginationRef,
        selectedPreset,
        setSelectedPreset,
        columnPresets,
    } = React.useContext(SearchContext) as SearchContextType;
    const { clinic } = useSearchClinic();
    const { PCP } = useSearchPCP();
    const { sort, updateSort } = useSearchSort();
    const history = useHistory();
    const filters = useLocationFilters();
    const [loading, setLoading] = React.useState(false);
    const [patients, setPatients] = React.useState<TODO_TYPEME[]>([]);
    const initialLoad = React.useRef(true);
    const disableRedirect = filters.some((x) => x[0] === 'redirect' && x[1] === 'false');
    const columnPreset = columnPresets.find((x) => x.label === selectedPreset);
    const labeledColumnFields: LabeledColumnField[] =
        columnPreset?.columns
            .filter(
                (col) =>
                    !(context.user.hasFeatureToggle('hidden_foc') && col.substring(0, 2) === 'AW')
            )
            .map((col) => ({
                label: col,
                field: colFieldMap[col],
            })) || [];

    const loadResults = React.useCallback(async () => {
        // On the first page load, check if we have any updates
        // from the page location filters that came externally
        if (initialLoad.current && hasUnsavedFilters(filters, searchValues)) {
            // Set the values, but then bail before the query runs,
            // so that the query will pick up on the next render
            setSearchValues((prev) => populateSearchFromQuery(filters, prev));
            return;
        }
        let query = `associations[pcps][]&associations[phones][]&associations[insurance_plans][]&${prepareSearchQueryString(
            {
                ...searchValues,
                clinic,
                PCP,
            },
            true
        )}`;
        const isMIC = filters.some((x) => x[0] === 'mic' && x[1] === 'true') && initialLoad.current;
        initialLoad.current = false;

        // Add in any status filters from the top stats selections
        filters.forEach(([key, value]) => {
            if (key.includes('_gaps.status') || key.includes('gap_totals')) {
                query += `&${key}=${value}`;
            }
        });

        if (sort.field !== '') {
            query += `&order[${sort.field}]=${sort.dir}`.replace('patient_record', 'pr');
        }
        if (paginationRef.current.current !== null) {
            query += `&page=${paginationRef.current.current}`;
        }
        if (paginationRef.current.items_per_page !== null) {
            query += `&items_per_page=${paginationRef.current.items_per_page}`;
        }
        window.scrollTo(0, 0);
        setLoading(true);
        try {
            const result = await context.api.call({
                method: 'get',
                url: `/api/patients?${query}`,
                data: {},
                headers: {
                    'Content-Type': 'application/ld+json',
                    'x-mic-action': isMIC ? true : null,
                },
            });
            setLoading(false);
            paginationRef.current = convertPagination(result.data);
            setPatients(result.data['hydra:member']);

            // For MIC, send a message to the extension if we got a single match from the search
            if (isMIC && result.data['hydra:member'].length === 1) {
                window.postMessage({ method: 'singlePatientMatch' }, window.location.origin);
            }
        } catch (e) {
            setLoading(false);
        }
        // Since paginationRef is declared in the context, eslint doesn't know
        // it's actually a ref and can be excluded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValues, sort, filters, clinic, PCP, context.api]);
    React.useEffect(() => {
        loadResults();
    }, [loadResults, reportYear]);

    const initHeaders = React.useCallback(() => {
        // Check if we have any searched values that should toggle
        // context specific headers, otherwise use the Pending default
        if (
            !disableRedirect &&
            !filters.some((x) => {
                if (x[0].includes('risk_band_level')) {
                    setSelectedPreset('Risk Band');
                    return true;
                }
                if (x[0].includes('risk_gaps.status')) {
                    setSelectedPreset('HCC/ICD');
                    return true;
                }
                if (x[0].includes('foc_gaps.status')) {
                    setSelectedPreset('AW');
                    return true;
                }
                if (x[0].includes('quality_gaps.status')) {
                    setSelectedPreset('Quality');
                    return true;
                }
                if (x[0].includes('oldest_answer_at')) {
                    setSelectedPreset('In Progress');
                    return true;
                }
                return false;
            })
        )
            setSelectedPreset('Pending');
    }, [filters, setSelectedPreset, disableRedirect]);
    React.useEffect(() => {
        initHeaders();
    }, [initHeaders, reportYear]);

    async function updatePagination(pagination: PaginationType) {
        paginationRef.current = pagination;
        await loadResults();
    }

    if (!disableRedirect && patients.length === 1 && paginationRef.current.current === 1)
        return <PatientRedirect patientId={patients[0].id} />;

    function applicationBack() {
        history.push('/dashboard');
    }
    return (
        <Card
            title={
                <div className="row">
                    <div className="col">
                        <BackButton applicationBack={applicationBack} />
                        Patient Search Results
                    </div>
                    <div className="col text-end">
                        <SearchFiltersModal />
                    </div>
                </div>
            }
            className="search-results"
        >
            <div className="row align-items-center mb-3">
                <FilterBadges />
                <div className="col-3 header-select d-none d-lg-block">
                    <Select
                        label="Row header options"
                        options={columnPresets.map((x) => ({
                            value: x.label,
                            label: x.label,
                        }))}
                        value={{ value: selectedPreset, label: selectedPreset }}
                        onChange={(h) => setSelectedPreset(h!.value)}
                        className="text-start"
                    />
                </div>
            </div>
            {!loading && patients.length === 0 ? (
                <div className="text-center mt-5 mb-5">
                    <p>
                        There are no results
                        <br />
                        for your current search
                    </p>
                    <p>
                        <BackButton
                            applicationBack={() => history.push('/dashboard')}
                            showIcon={false}
                        >
                            Try different search?
                        </BackButton>
                    </p>
                </div>
            ) : (
                <div className="search-patient-content">
                    <SpinnerOverlay show={loading} />
                    <div className="table-scroll search-patient-scroll">
                        <table className="table table-striped table-bordered">
                            <thead className="table-dark">
                                <tr>
                                    {[
                                        { label: 'First Name', field: 'first_name', className: '' },
                                        { label: 'Last Name', field: 'last_name', className: '' },
                                        { label: 'DOB', field: 'dob', className: '' },
                                        ...labeledColumnFields.map((x) => ({
                                            ...x,
                                            className: 'd-none d-lg-table-cell',
                                        })),
                                    ].map((x) => (
                                        <ResultsHeader
                                            key={x.label}
                                            updateSort={updateSort}
                                            label={x.label}
                                            field={x.field}
                                            className={x.className}
                                            sort={sort}
                                        />
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {patients.map((patient) => (
                                    <ErrorBoundary
                                        FallbackComponent={ResultsRowFallback}
                                        onError={logError}
                                        key={patient.id}
                                    >
                                        <ResultsRow
                                            patient={patient}
                                            key={patient.id}
                                            labeledColumnFields={labeledColumnFields}
                                        />
                                    </ErrorBoundary>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        pagination={paginationRef.current}
                        updatePagination={updatePagination}
                    />
                </div>
            )}
        </Card>
    );
}
